/**
 * Pure Tab-key boundary wrap shared by the warning dialog (`warning.ts`) and
 * the scare overlay (`scene.ts`). Both are small modal dialogs with a known
 * first and last focusable element; this only handles the boundary case —
 * Tab past the last element, or Shift+Tab past the first — and leaves every
 * other key, and every in-between Tab, to the browser's own tab order.
 *
 * `event` is a minimal event-like shape (not a real `KeyboardEvent`) so this
 * stays pure and unit-testable without a DOM. `activeEl` is compared to
 * `firstEl`/`lastEl` by reference (`===`), so callers pass whatever identity
 * the platform's "currently focused thing" is — typically
 * `document.activeElement`. A dialog with a single focusable target passes
 * the same element as both `firstEl` and `lastEl`, which keeps Tab trapped
 * on it (each boundary hit just refocuses itself).
 */

export interface FocusTrapKeyEvent {
  readonly key: string;
  readonly shiftKey: boolean;
  preventDefault(): void;
}

export interface FocusTrapTarget {
  focus(): void;
}

export function trapTabKey(
  event: FocusTrapKeyEvent,
  firstEl: FocusTrapTarget,
  lastEl: FocusTrapTarget,
  activeEl: unknown,
): void {
  if (event.key !== "Tab") return;

  if (event.shiftKey) {
    if (activeEl !== firstEl) return;
    event.preventDefault();
    lastEl.focus();
    return;
  }

  if (activeEl !== lastEl) return;
  event.preventDefault();
  firstEl.focus();
}
